import useEmblaCarousel from 'embla-carousel-react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  Mail,
  ShoppingCart,
  Truck,
  Video,
} from 'lucide-react'
import {
  combineSuggestions,
  confForPerMessageSuggestionStrip,
  isEngagementLeadFormStub,
  withOchatbotEcommerceAddToCartTracking,
  withOchatbotEcommerceViewTracking,
} from '../../lib/chatUtils.js'
import { fetchWidgetLeadFormById } from '../../lib/leadFormsApi.js'
import { t as localeT } from '../../lib/widgetLocale.js'
import {
  clampStandardCardWidthToViewport,
  normalizeSuggestionStripSize,
  resolveLandscapeImageBandPx,
  resolveSuggestionCardScale,
  resolveSuggestionStripSizeTokens,
  SUGGESTION_CARD_BORDER_CLASS,
  SUGGESTION_SIDE_COLUMN_IMAGE_MIN_PX,
  SUGGESTION_STRIP_CARD_WIDTH_PX,
  suggestionCardTypography,
  suggestionStripCardHeightPx,
  suggestionSubtitleClampWithLandscapeBonus,
} from '../../lib/suggestionCardLayout.js'
import { useIntelligentWeb } from '../../context/IntelligentWebContext.jsx'
import { useForm } from '../../context/FormContext.jsx'
import { useOrderStatus } from '../../context/OrderStatusContext.jsx'
import { SuggestionCardTooltip } from '../ui/SuggestionCardTooltip.jsx'
import { cn } from '../../lib/cn.js'

function stripHtml(html) {
  if (html == null || typeof html !== 'string') return ''
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}

function suggestionCardFrameProps(sideColumn, layoutInline, layoutKey, stripSize = 'large') {
  if (sideColumn) {
    return {
      imageBandPx: null,
      minLandscapeBandPx: SUGGESTION_SIDE_COLUMN_IMAGE_MIN_PX,
      frameStyle: undefined,
      frameClass: 'h-full min-h-0 w-full max-h-full',
    }
  }
  const size = normalizeSuggestionStripSize(stripSize)
  const tokens = resolveSuggestionStripSizeTokens(size)
  const cardHeightPx = suggestionStripCardHeightPx(layoutKey, size)
  return {
    imageBandPx: tokens.imageBandPx,
    minLandscapeBandPx: tokens.minLandscapeBandPx,
    frameStyle: { height: cardHeightPx, maxHeight: cardHeightPx },
    frameClass: cn(
      layoutInline ? 'w-full min-w-0' : 'w-[240px] max-w-[240px] shrink-0',
    ),
  }
}

function suggestionImageBandClass(sideColumn, imagePad) {
  return cn(
    sideColumn
      ? 'flex flex-1 basis-[42%] max-h-[52%] flex-col border-b border-[#f8f9fa] bg-[#ffffff] dark:border-[var(--color-border)] dark:bg-[var(--color-muted)]'
      : 'shrink-0 border-b border-[#f8f9fa] bg-[#ffffff] dark:border-[var(--color-border)] dark:bg-[var(--color-muted)]',
    imagePad,
  )
}

/** Lead-form and order-status cards: centered stack with even spacing (not pinned top/bottom). */
function SuggestionCenteredActionBody({ typography: t, title, excerpt, button }) {
  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col items-center justify-center text-center',
        t.actionPad,
        t.actionStackGap,
      )}
    >
      <div className={cn('flex w-full max-w-full flex-col items-center', t.actionTextGap)}>
        {title}
        {excerpt}
      </div>
      {button}
    </div>
  )
}

function suggestionImageBandStyle(sideColumn) {
  return sideColumn
    ? { minHeight: SUGGESTION_SIDE_COLUMN_IMAGE_MIN_PX }
    : undefined
}

function firstNonEmptyString(...values) {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

function resolveActionCardImageUrls(customCandidates, conf) {
  const custom = firstNonEmptyString(...customCandidates)
  const defaultImage = firstNonEmptyString(conf?.defaultCardImage)
  return {
    imageUrl: custom || defaultImage,
    fallbackImageUrl:
      custom && defaultImage && custom !== defaultImage ? defaultImage : '',
  }
}

function resolveLeadformCardImageUrls(item, conf) {
  const cfg =
    item?.config && typeof item.config === 'object' && !Array.isArray(item.config)
      ? item.config
      : {}
  return resolveActionCardImageUrls(
    [
      cfg.cardImageUrl,
      cfg.bannerImage,
      cfg.image,
      item?.image_url,
      item?.image,
    ],
    conf,
  )
}

function resolveOrderStatusCardImageUrls(item, conf) {
  return resolveActionCardImageUrls(
    [
      item?.image_url,
      item?.image,
      item?.cardImageUrl,
      conf?.orderStatus?.cardImageUrl,
    ],
    conf,
  )
}

/** Resolve media/CTA card colors (flat keys + builder `config.colors` nest). */
export function resolveCtaThemeColors(config = {}, conf = {}) {
  const nested =
    config.colors && typeof config.colors === 'object' ? config.colors : {}
  return {
    headlineColor: config.headlineColor || nested.headline || '#000000',
    descriptionColor: config.descriptionColor || nested.description || '#666666',
    buttonColor:
      config.buttonColor ||
      nested.buttonBg ||
      nested.button ||
      conf?.widgetButtonColor?.trim?.() ||
      '#1976d2',
    buttonTextColor:
      config.buttonTextColor ||
      nested.buttonText ||
      conf?.widgetButtonTextColor?.trim?.() ||
      '#ffffff',
  }
}

function SuggestionCardImageBand({
  imageUrl,
  fallbackImageUrl,
  alt,
  sideColumn,
  typography: t,
  imageBandPx,
  minLandscapeBandPx,
  onEffectiveBandChange,
  fallbackIcon: FallbackIcon,
}) {
  const [failedPrimary, setFailedPrimary] = useState(false)
  const [failedFallback, setFailedFallback] = useState(false)
  const [effectiveBandPx, setEffectiveBandPx] = useState(imageBandPx)

  useEffect(() => {
    setFailedPrimary(false)
    setFailedFallback(false)
    setEffectiveBandPx(imageBandPx)
    onEffectiveBandChange?.(imageBandPx, false)
  }, [imageUrl, fallbackImageUrl, imageBandPx, onEffectiveBandChange])

  const tryFallbackImage =
    Boolean(fallbackImageUrl) &&
    fallbackImageUrl !== imageUrl &&
    !failedFallback
  const showPrimary = Boolean(imageUrl) && !failedPrimary
  const showFallback = !showPrimary && tryFallbackImage
  const activeImageUrl = showPrimary ? imageUrl : showFallback ? fallbackImageUrl : ''
  const showImage = Boolean(activeImageUrl)
  const bandPx =
    sideColumn || imageBandPx == null
      ? imageBandPx
      : effectiveBandPx != null
        ? effectiveBandPx
        : imageBandPx

  const handleImageLoad = (event) => {
    if (sideColumn || imageBandPx == null) return
    const img = event.currentTarget
    const next = resolveLandscapeImageBandPx({
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight,
      defaultBandPx: imageBandPx,
      cardWidthPx: SUGGESTION_STRIP_CARD_WIDTH_PX,
      minBandPx: minLandscapeBandPx,
    })
    setEffectiveBandPx(next)
    onEffectiveBandChange?.(next, next < imageBandPx)
  }

  return (
    <div
      className={suggestionImageBandClass(sideColumn, t.imagePad)}
      style={suggestionImageBandStyle(sideColumn)}
    >
      {showImage ? (
        <img
          src={activeImageUrl}
          alt={alt}
          style={
            bandPx != null
              ? { height: bandPx, maxHeight: bandPx }
              : undefined
          }
          className={cn(
            'mx-auto w-full max-w-full object-contain',
            sideColumn && 'min-h-0 flex-1',
          )}
          loading="lazy"
          onLoad={handleImageLoad}
          onError={() => {
            if (showPrimary) setFailedPrimary(true)
            else if (showFallback) setFailedFallback(true)
          }}
        />
      ) : (
        <div
          style={bandPx != null ? { height: bandPx } : undefined}
          className={cn(
            'flex w-full flex-1 items-center justify-center text-[var(--color-muted-foreground)]',
            sideColumn && 'min-h-0',
          )}
        >
          <FallbackIcon className={cn(t.fallbackIcon)} />
        </div>
      )}
    </div>
  )
}

function SuggestionProductOrPageCard({
  item,
  conf,
  layoutInline = false,
  layoutKey = null,
  onActivate,
  ochatbot,
  sideColumn = false,
  fullWidthButtons = false,
  stripSize = 'large',
}) {
  const size = sideColumn ? 'large' : normalizeSuggestionStripSize(stripSize)
  const t = suggestionCardTypography(
    resolveSuggestionCardScale({ sideColumn, layoutInline, layoutKey }),
    size,
  )
  const [landscapeShrunk, setLandscapeShrunk] = useState(false)
  const [effectiveBandPx, setEffectiveBandPx] = useState(null)
  const isProduct = item.type === 'product'
  const title = item.title || ''
  const subtitle = item.subtitle || item.excerpt || ''
  const plainTitle = stripHtml(title)
  const plainSubtitle = stripHtml(subtitle)
  const imageUrl =
    item.image_url ||
    item.image ||
    item.featured_image ||
    item.featuredImage ||
    item.product_image ||
    item.productImage ||
    conf?.defaultCardImage ||
    ''
  const buttons = item.buttons || []
  const viewUrl = isProduct
    ? buttons[0]?.url || item.url || item.item_url
    : item.url || buttons[0]?.url
  const addToCartUrl = isProduct ? item.add_to_cart_url : null

  const handleAddToCart = (e) => {
    e.stopPropagation()
    if (!addToCartUrl || !ochatbot) return
    const cartUrl = withOchatbotEcommerceAddToCartTracking(addToCartUrl)
    ochatbot.callAPI(
      `ometrics-button-click~${cartUrl}`,
      plainTitle || title || '',
      true,
    )
    window.open(cartUrl, '_blank', 'noopener,noreferrer')
  }

  const openPrimary = () => onActivate(item)

  /** Matches v1 `MobileProductCard` / `MobilePageCard`: first button title from API when present. */
  const fromBtnTitle =
    typeof buttons[0]?.title === 'string'
      ? stripHtml(buttons[0].title).trim()
      : ''
  const viewButtonLabel =
    fromBtnTitle || (isProduct ? 'View Details' : 'Read More')

  const buttonSurfaceStyle = {
    backgroundColor: conf?.widgetButtonColor || '#1976d2',
    color: conf?.widgetButtonTextColor || '#ffffff',
  }

  const showFooter = Boolean(viewUrl || addToCartUrl)
  const { imageBandPx, minLandscapeBandPx, frameStyle, frameClass } =
    suggestionCardFrameProps(sideColumn, layoutInline, layoutKey, size)

  useEffect(() => {
    setEffectiveBandPx(imageBandPx)
    setLandscapeShrunk(false)
  }, [imageBandPx, imageUrl])

  const bandPx =
    sideColumn || imageBandPx == null
      ? imageBandPx
      : effectiveBandPx != null
        ? effectiveBandPx
        : imageBandPx

  const handleImageLoad = (event) => {
    if (sideColumn || imageBandPx == null) return
    const img = event.currentTarget
    const next = resolveLandscapeImageBandPx({
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight,
      defaultBandPx: imageBandPx,
      cardWidthPx: SUGGESTION_STRIP_CARD_WIDTH_PX,
      minBandPx: minLandscapeBandPx,
    })
    setEffectiveBandPx(next)
    setLandscapeShrunk(next < imageBandPx)
  }

  const subtitleClamp = suggestionSubtitleClampWithLandscapeBonus(
    t.subtitleClamp,
    landscapeShrunk && !sideColumn,
  )

  return (
    <div
      style={frameStyle}
      className={cn(
        'flex flex-col overflow-hidden bg-[var(--color-surface)] text-left',
        !sideColumn && 'shrink-0',
        SUGGESTION_CARD_BORDER_CLASS,
        // Side (overlay): avoid hover translate here — it stacks with the Embla
        // slide scale and can read as horizontal jitter; shadow reads the same affordance.
        sideColumn
          ? 'transition-shadow duration-200 hover:shadow-md'
          : 'transition-transform duration-200 hover:-translate-y-0.5',
        frameClass,
      )}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={openPrimary}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            openPrimary()
          }
        }}
        className={cn(
          'flex min-h-0 min-w-0 flex-1 cursor-pointer flex-col overflow-hidden outline-none',
          'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-brand)]',
        )}
      >
        <div
          className={suggestionImageBandClass(sideColumn, t.imagePad)}
          style={suggestionImageBandStyle(sideColumn)}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={plainTitle}
              style={
                bandPx != null
                  ? { height: bandPx, maxHeight: bandPx }
                  : undefined
              }
              className={cn(
                'mx-auto w-full max-w-full object-contain',
                sideColumn && 'min-h-0 flex-1',
              )}
              loading="lazy"
              onLoad={handleImageLoad}
            />
          ) : (
            <div
              style={bandPx != null ? { height: bandPx } : undefined}
              className={cn(
                'flex w-full flex-1 items-center justify-center text-[var(--color-muted-foreground)]',
                sideColumn && 'min-h-0',
              )}
            >
              {isProduct ? (
                <ShoppingCart className={cn(t.fallbackIcon)} />
              ) : (
                <FileText className={cn(t.fallbackIcon)} />
              )}
            </div>
          )}
        </div>

        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col overflow-hidden',
            t.bodyGap,
            t.bodyPad,
          )}
        >
          {title ? (
            <SuggestionCardTooltip
              title={plainTitle}
              description={plainSubtitle}
              className="min-w-0 shrink-0"
            >
              <div
                className={cn(
                  'line-clamp-2 text-[#212529] dark:text-[var(--color-surface-foreground)]',
                  t.title,
                )}
                dangerouslySetInnerHTML={{ __html: title }}
              />
            </SuggestionCardTooltip>
          ) : null}
          {isProduct && (item.price || item.sale_price) ? (
            <div className="flex shrink-0 flex-wrap items-baseline gap-1">
              {item.sale_price ? (
                <span className={cn(t.price, 'text-[#dc3545]')}>
                  {item.sale_price}
                </span>
              ) : null}
              {item.price ? (
                <span
                  className={cn(
                    t.price,
                    item.sale_price
                      ? 'text-[#6c757d] line-through'
                      : 'text-[#212529] dark:text-[var(--color-surface-foreground)]',
                  )}
                >
                  {item.price}
                </span>
              ) : null}
            </div>
          ) : null}
          {subtitle ? (
            <SuggestionCardTooltip
              title={plainTitle}
              description={plainSubtitle}
              className="min-w-0"
            >
              <p
                className={cn(
                  'min-h-[1.25em] text-[#6c757d] dark:text-[var(--color-muted-foreground)]',
                  t.subtitle,
                  subtitleClamp,
                )}
              >
                {plainSubtitle}
              </p>
            </SuggestionCardTooltip>
          ) : null}
        </div>
      </div>

      {showFooter ? (
        <div
          className={cn(
            'flex shrink-0 gap-2 border-t border-[var(--color-border)] px-2 py-2',
            fullWidthButtons
              ? 'flex-col'
              : 'flex-wrap justify-center',
          )}
        >
          {viewUrl ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                openPrimary()
              }}
              title={viewUrl}
              className={cn(
                'inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-full text-center font-medium transition-opacity hover:opacity-90',
                t.pillBtn,
                fullWidthButtons ? 'w-full shrink-0' : 'shrink-0',
              )}
              style={buttonSurfaceStyle}
            >
              {viewButtonLabel}
            </button>
          ) : null}
          {addToCartUrl ? (
            <button
              type="button"
              onClick={handleAddToCart}
              title={addToCartUrl}
              className={cn(
                'inline-flex cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-full font-medium transition-opacity hover:opacity-90',
                t.pillBtn,
                fullWidthButtons ? 'w-full shrink-0' : 'shrink-0',
              )}
              style={buttonSurfaceStyle}
            >
              <ShoppingCart className="h-3.5 w-3.5 shrink-0" />
              Add to Cart
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function SuggestionMediaButtonCard({
  item,
  conf,
  layoutInline = false,
  layoutKey = null,
  onActivate,
  sideColumn = false,
  fullWidthButtons = false,
  stripSize = 'large',
}) {
  const size = sideColumn ? 'large' : normalizeSuggestionStripSize(stripSize)
  const t = suggestionCardTypography(
    resolveSuggestionCardScale({ sideColumn, layoutInline, layoutKey }),
    size,
  )
  const { frameStyle, frameClass } = suggestionCardFrameProps(
    sideColumn,
    layoutInline,
    layoutKey,
    size,
  )
  const cfg =
    item.config && typeof item.config === 'object' && !Array.isArray(item.config)
      ? item.config
      : {}
  const theme = resolveCtaThemeColors(cfg, conf)
  const headline =
    typeof cfg.headline === 'string' ? cfg.headline.trim() : ''
  const description =
    typeof cfg.description === 'string' ? cfg.description.trim() : ''
  const buttonText =
    typeof cfg.buttonText === 'string' && cfg.buttonText.trim()
      ? cfg.buttonText.trim()
      : 'Click Here'

  return (
    <div
      style={frameStyle}
      className={cn(
        'flex cursor-default flex-col overflow-hidden bg-white text-center dark:bg-[var(--color-surface)]',
        !sideColumn && 'shrink-0',
        SUGGESTION_CARD_BORDER_CLASS,
        !sideColumn && 'transition-transform duration-200 hover:-translate-y-0.5',
        sideColumn && 'min-h-0 flex-1',
        frameClass,
      )}
    >
      <SuggestionCenteredActionBody
        typography={t}
        title={
          headline ? (
            <p
              className={cn('line-clamp-2 w-full break-words', t.actionTitle)}
              style={{ color: theme.headlineColor }}
            >
              {stripHtml(String(headline))}
            </p>
          ) : null
        }
        excerpt={
          description ? (
            <p
              className={cn('line-clamp-3 w-full break-words', t.actionExcerpt)}
              style={{ color: theme.descriptionColor }}
            >
              {stripHtml(String(description))}
            </p>
          ) : null
        }
        button={
          <button
            type="button"
            onClick={() => onActivate(item)}
            className={cn(
              'inline-flex cursor-pointer items-center justify-center rounded-full font-medium transition-opacity',
              t.pillBtn,
              'hover:opacity-90',
              fullWidthButtons ? 'w-full max-w-full shrink-0' : 'min-w-[100px] max-w-full shrink-0',
            )}
            style={{
              backgroundColor: theme.buttonColor,
              color: theme.buttonTextColor,
            }}
          >
            {stripHtml(String(buttonText))}
          </button>
        }
      />
    </div>
  )
}

function SuggestionCtaCard({
  item,
  layoutInline = false,
  layoutKey = null,
  onActivate,
  sideColumn = false,
  stripSize = 'large',
}) {
  const size = sideColumn ? 'large' : normalizeSuggestionStripSize(stripSize)
  const t = suggestionCardTypography(
    resolveSuggestionCardScale({ sideColumn, layoutInline, layoutKey }),
    size,
  )
  const { frameStyle, frameClass } = suggestionCardFrameProps(
    sideColumn,
    layoutInline,
    layoutKey,
    size,
  )
  const cfg =
    item.config && typeof item.config === 'object' && !Array.isArray(item.config)
      ? item.config
      : {}
  const imageUrl =
    item.image_url ||
    item.image ||
    cfg.bannerImage ||
    cfg.image ||
    ''
  const imageAlt = item.type === 'video' ? 'Video' : 'Image'
  const FallbackIcon = item.type === 'video' ? Video : FileText

  return (
    <button
      type="button"
      onClick={() => onActivate(item)}
      style={frameStyle}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center overflow-hidden bg-[var(--color-surface)] shadow-sm transition-shadow hover:shadow-md',
        !sideColumn && 'shrink-0',
        SUGGESTION_CARD_BORDER_CLASS,
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]',
        frameClass,
      )}
    >
      {imageUrl ? (
        <div
          className={cn(
            'flex h-full w-full min-h-0 flex-1 items-center justify-center bg-[#ffffff] dark:bg-[var(--color-muted)]',
            t.imagePad,
          )}
        >
          <img
            src={imageUrl}
            alt={stripHtml(String(imageAlt))}
            className="max-h-full max-w-full object-contain"
            loading="lazy"
          />
        </div>
      ) : (
        <div
          className={cn(
            'flex h-full w-full flex-1 items-center justify-center text-[var(--color-muted-foreground)]',
            t.imagePad,
          )}
        >
          <FallbackIcon className={cn(t.fallbackIcon)} aria-hidden />
        </div>
      )}
    </button>
  )
}

function SuggestionOrderStatusCard({
  item,
  conf,
  layoutInline = false,
  layoutKey = null,
  onActivate,
  sideColumn = false,
  fullWidthButtons = false,
  stripSize = 'large',
}) {
  const size = sideColumn ? 'large' : normalizeSuggestionStripSize(stripSize)
  const t = suggestionCardTypography(
    resolveSuggestionCardScale({ sideColumn, layoutInline, layoutKey }),
    size,
  )
  const { imageBandPx, minLandscapeBandPx, frameStyle, frameClass } =
    suggestionCardFrameProps(sideColumn, layoutInline, layoutKey, size)
  const { isOrderStatusOpen } = useOrderStatus()
  const lang = conf?.siteLanguage
  const title = item.title || localeT('orderStatus.formTitle', lang)
  const excerpt = item.excerpt || ''
  const { imageUrl, fallbackImageUrl } = resolveOrderStatusCardImageUrls(item, conf)
  const submitLabel =
    typeof item.submitLabel === 'string' && item.submitLabel.trim()
      ? item.submitLabel.trim()
      : localeT('orderStatus.submitLabel', lang)
  const buttonColor =
    conf?.widgetButtonColor?.trim?.() || '#1976d2'
  const buttonTextColor =
    conf?.widgetButtonTextColor?.trim?.() || '#ffffff'

  return (
    <div
      style={frameStyle}
      className={cn(
        'flex cursor-default flex-col overflow-hidden bg-white text-center dark:bg-[var(--color-surface)]',
        !sideColumn && 'shrink-0',
        SUGGESTION_CARD_BORDER_CLASS,
        !sideColumn && 'transition-transform duration-200 hover:-translate-y-0.5',
        sideColumn && 'min-h-0 flex-1',
        frameClass,
      )}
    >
      <SuggestionCardImageBand
        imageUrl={imageUrl}
        fallbackImageUrl={fallbackImageUrl}
        alt={stripHtml(String(title))}
        sideColumn={sideColumn}
        typography={t}
        imageBandPx={imageBandPx}
        minLandscapeBandPx={minLandscapeBandPx}
        fallbackIcon={Truck}
      />
      <SuggestionCenteredActionBody
        typography={t}
        title={
          <p
            className={cn(
              'line-clamp-2 w-full break-words text-[var(--color-surface-foreground)]',
              t.actionTitle,
            )}
          >
            {stripHtml(String(title))}
          </p>
        }
        excerpt={
          excerpt ? (
            <p
              className={cn(
                'line-clamp-3 w-full break-words text-[#666666] dark:text-[var(--color-muted-foreground)]',
                t.actionExcerpt,
              )}
            >
              {stripHtml(String(excerpt))}
            </p>
          ) : null
        }
        button={
          <button
            type="button"
            onClick={() => onActivate(item)}
            disabled={isOrderStatusOpen}
            className={cn(
              'inline-flex cursor-pointer items-center justify-center rounded-full font-medium transition-opacity',
              t.pillBtn,
              'hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[#ccc] disabled:text-[#666] disabled:opacity-100',
              fullWidthButtons ? 'w-full max-w-full shrink-0' : 'min-w-[100px] max-w-full shrink-0',
            )}
            style={{
              backgroundColor: isOrderStatusOpen ? undefined : buttonColor,
              color: isOrderStatusOpen ? undefined : buttonTextColor,
            }}
          >
            {isOrderStatusOpen ? localeT('form.formOpen', lang) : stripHtml(String(submitLabel))}
          </button>
        }
      />
    </div>
  )
}

function SuggestionLeadformCard({
  item,
  conf,
  layoutInline = false,
  layoutKey = null,
  onOpen,
  sideColumn = false,
  fullWidthButtons = false,
  stripSize = 'large',
}) {
  const size = sideColumn ? 'large' : normalizeSuggestionStripSize(stripSize)
  const t = suggestionCardTypography(
    resolveSuggestionCardScale({ sideColumn, layoutInline, layoutKey }),
    size,
  )
  const { imageBandPx, minLandscapeBandPx, frameStyle, frameClass } =
    suggestionCardFrameProps(sideColumn, layoutInline, layoutKey, size)
  const { activeForm, isFormOpen } = useForm()
  const lang = conf?.siteLanguage
  const cfg = item.config || {}
  const headline = cfg.headline
  const description = cfg.description
  const buttonText = cfg.buttonText || localeT('form.contactUs', lang)
  const headlineColor = cfg.headlineColor || '#000000'
  const descriptionColor = cfg.descriptionColor || '#666666'
  const buttonColor = cfg.buttonColor || '#1976d2'
  const buttonTextColor = cfg.buttonTextColor || '#ffffff'
  const fallbackTitle = item.title || item.name || localeT('form.contact', lang)
  const isActive = isFormOpen && activeForm?.id === item.id
  const { imageUrl, fallbackImageUrl } = resolveLeadformCardImageUrls(item, conf)
  const cardTitle = headline || fallbackTitle

  return (
    <div
      style={frameStyle}
      className={cn(
        'flex cursor-default flex-col overflow-hidden bg-white text-center dark:bg-[var(--color-surface)]',
        !sideColumn && 'shrink-0',
        SUGGESTION_CARD_BORDER_CLASS,
        !sideColumn && 'transition-transform duration-200 hover:-translate-y-0.5',
        sideColumn && 'min-h-0 flex-1',
        frameClass,
      )}
    >
      <SuggestionCardImageBand
        imageUrl={imageUrl}
        fallbackImageUrl={fallbackImageUrl}
        alt={stripHtml(String(cardTitle))}
        sideColumn={sideColumn}
        typography={t}
        imageBandPx={imageBandPx}
        minLandscapeBandPx={minLandscapeBandPx}
        fallbackIcon={Mail}
      />
      <SuggestionCenteredActionBody
        typography={t}
        title={
          headline ? (
            <p
              className={cn('line-clamp-2 w-full break-words', t.actionTitle)}
              style={{ color: headlineColor }}
            >
              {stripHtml(String(headline))}
            </p>
          ) : (
            <p
              className={cn(
                'line-clamp-2 w-full break-words text-[var(--color-surface-foreground)]',
                t.actionTitle,
              )}
            >
              {stripHtml(String(fallbackTitle))}
            </p>
          )
        }
        excerpt={
          description ? (
            <p
              className={cn('line-clamp-3 w-full break-words', t.actionExcerpt)}
              style={{ color: descriptionColor }}
            >
              {stripHtml(String(description))}
            </p>
          ) : null
        }
        button={
          <button
            type="button"
            onClick={() => onOpen(item)}
            disabled={isActive}
            className={cn(
              'inline-flex cursor-pointer items-center justify-center rounded-full font-medium transition-opacity',
              t.pillBtn,
              'hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[#ccc] disabled:text-[#666] disabled:opacity-100',
              fullWidthButtons ? 'w-full max-w-full shrink-0' : 'min-w-[100px] max-w-full shrink-0',
            )}
            style={{
              backgroundColor: isActive ? undefined : buttonColor,
              color: isActive ? undefined : buttonTextColor,
            }}
          >
            {isActive ? localeT('form.formOpen', lang) : stripHtml(String(buttonText))}
          </button>
        }
      />
    </div>
  )
}

function SuggestionTextChip({ item, onActivate, sideColumn = false }) {
  const label = item.title || item.text || item.excerpt || 'Suggest'
  return (
    <button
      type="button"
      onClick={() => onActivate(item)}
      className={cn(
        'shrink-0 rounded-xl border border-[var(--color-border)] bg-[var(--color-muted)] px-3 py-1.5 text-[12px] font-medium text-[var(--color-surface-foreground)] transition-colors hover:bg-[var(--color-border)]',
        sideColumn ? 'w-full py-2 text-center' : 'text-left',
      )}
    >
      {stripHtml(String(label))}
    </button>
  )
}

/**
 * Carousel of combined suggestions (dynamic + lead forms + CTAs), matching
 * v1 `SuggestionsPanel` / `MobileSuggestionsStrip` data model. Uses Embla in
 * the shadow DOM. `variant="side"` is the overlay right column: horizontal
 * center-mode carousel like v1 (peek neighbors, dim non-center slides).
 */
export function SuggestionsStrip({
  suggestions = [],
  conf: confProp,
  variant = 'panel',
  /** When `variant="inline"`, slide width is capped to a standard card width (see `suggestionCardLayout`). */
  layoutKey = null,
  /** Global CTAs, lead forms, and order-status card (latest bot turn only for inline). */
  includeAdminItems = true,
  onSend,
  ochatbot,
}) {
  const { conf: ctxConf, activeCtas, activeLeadForms, setActiveLeadForms } = useIntelligentWeb()
  const { openForm } = useForm()
  const { openOrderStatus } = useOrderStatus()
  const conf = confProp || ctxConf
  const layoutInline = variant === 'inline'
  const compact = variant === 'inline' && layoutKey !== 'slide-out' && layoutKey !== 'mobile'
  const isSide = variant === 'side'
  const isSlideOut = layoutKey === 'slide-out'
  const fullWidthButtons = isSlideOut && !isSide
  const targetColumn = isSide ? 'right' : 'left'
  const stripSize = isSide
    ? 'large'
    : normalizeSuggestionStripSize(conf?.suggestionStripSize)

  const openFormFromStrip = useCallback(
    (form) => openForm(form, { column: targetColumn }),
    [openForm, targetColumn],
  )

  const allItems = useMemo(() => {
    if (layoutInline && !includeAdminItems) {
      return combineSuggestions(
        suggestions,
        [],
        [],
        confForPerMessageSuggestionStrip(conf),
      )
    }
    return combineSuggestions(suggestions, activeCtas, activeLeadForms, conf)
  }, [
    layoutInline,
    includeAdminItems,
    suggestions,
    activeCtas,
    activeLeadForms,
    conf,
  ])

  /**
   * Embla only enables `loop: true` when `slideLooper.canLoop()` passes — that
   * requires enough total slide width relative to the viewport (often fails
   * with 2 cards in a wide strip). When loop is rejected, Embla silently
   * re-inits with loop disabled. We keep `loop: false` and wrap snaps manually.
   */
  const carouselOpts = useMemo(
    () => ({
      axis: 'x',
      align: isSide ? 'center' : 'start',
      containScroll: isSide ? false : 'trimSnaps',
      dragFree: false,
      loop: false,
      // Side (overlay) column: pointer movement toward footer buttons was
      // starting a horizontal drag, so the track scrolled and the card looked
      // like it jumped left. Chevrons + dots still change slides.
      watchDrag: !isSide,
    }),
    [isSide],
  )
  const [emblaRef, emblaApi] = useEmblaCarousel(carouselOpts)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    const enabled = allItems.length > 1
    setCanScrollPrev(enabled)
    setCanScrollNext(enabled)
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi, allItems.length])

  const scrollPrevCircular = useCallback(() => {
    if (!emblaApi) return
    const snaps = emblaApi.scrollSnapList()
    if (snaps.length <= 1) return
    const i = emblaApi.selectedScrollSnap()
    if (i <= 0) emblaApi.scrollTo(snaps.length - 1)
    else emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNextCircular = useCallback(() => {
    if (!emblaApi) return
    const snaps = emblaApi.scrollSnapList()
    if (snaps.length <= 1) return
    const i = emblaApi.selectedScrollSnap()
    const last = snaps.length - 1
    if (i >= last) emblaApi.scrollTo(0)
    else emblaApi.scrollNext()
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)
    // Avoid setState directly inside effect body (eslint rule).
    queueMicrotask(onSelect)
    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onSelect)
    }
  }, [emblaApi, onSelect])

  const itemsSignature = useMemo(
    () =>
      allItems
        .map(
          (i) =>
            `${i.type}:${String(i.id ?? '')}:${String(i.url ?? i.title ?? '')}`,
        )
        .join('|'),
    [allItems],
  )

  const syncInlineSlideBasis = useCallback(() => {
    if (!layoutInline || !emblaApi) return
    const node = emblaApi.rootNode()
    if (!(node instanceof HTMLElement)) return
    const w = node.getBoundingClientRect().width
    if (!(w > 0)) return
    node.style.setProperty(
      '--suggestions-inline-slide-basis',
      `${clampStandardCardWidthToViewport(w)}px`,
    )
  }, [emblaApi, layoutInline])

  useLayoutEffect(() => {
    if (!emblaApi || allItems.length === 0) return
    if (layoutInline) syncInlineSlideBasis()
    emblaApi.reInit()
    emblaApi.scrollTo(0)
  }, [emblaApi, itemsSignature, allItems.length, layoutInline, syncInlineSlideBasis])

  useEffect(() => {
    if (!layoutInline || !emblaApi || typeof ResizeObserver === 'undefined') return
    const node = emblaApi.rootNode()
    if (!node) return
    const ro = new ResizeObserver(() => {
      syncInlineSlideBasis()
      emblaApi.reInit()
    })
    ro.observe(node)
    return () => ro.disconnect()
  }, [emblaApi, layoutInline, syncInlineSlideBasis])

  const handleActivate = useCallback(
    (item) => {
      if (item.type === 'orderstatus') {
        openOrderStatus({ column: targetColumn })
        ochatbot?.callAPI(
          'ometrics-button-click~order-status-open',
          stripHtml(item.title || '') || 'Order status',
          true,
        )
        return
      }
      if (item.type === 'leadform') {
        if (!isEngagementLeadFormStub(item)) {
          openFormFromStrip(item)
          return
        }

        void (async () => {
          try {
            const list = await fetchWidgetLeadFormById({
              apiBaseUrl: conf?.apiBaseUrl,
              agentId: conf?.agentId || conf?.chatId,
              masterKey: conf?.masterKey,
              formId: item.id,
            })
            const fetched = Array.isArray(list)
              ? list.find((f) => String(f.id) === String(item.id)) || list[0]
              : null
            if (!fetched) return
            setActiveLeadForms?.((prev) => {
              const arr = Array.isArray(prev) ? prev : []
              if (arr.some((f) => String(f.id) === String(fetched.id))) return arr
              return [...arr, fetched]
            })
            openFormFromStrip(fetched)
          } catch (err) {
            console.error('SuggestionsStrip: lead form lookup failed', err)
          }
        })()
        return
      }
      if (item.type === 'text') {
        const t = item.title || item.text || item.excerpt
        if (t && onSend) onSend(stripHtml(t) || String(t))
        return
      }
      const ctaCfg =
        item.config && typeof item.config === 'object' && !Array.isArray(item.config)
          ? item.config
          : {}
      const url =
        item.url ||
        item.item_url ||
        (typeof ctaCfg.url === 'string' && ctaCfg.url.trim()) ||
        (Array.isArray(item.buttons) && item.buttons[0]?.url) ||
        item.add_to_cart_url
      if (url) {
        const trackedUrl =
          item.type === 'product'
            ? withOchatbotEcommerceViewTracking(url)
            : url
        ochatbot?.callAPI(
          `ometrics-button-click~${trackedUrl}`,
          stripHtml(item.title || '') || item.title || '',
          true,
        )
        window.open(trackedUrl, '_top', 'noopener,noreferrer')
        return
      }
      const t = item.title || item.text
      if (t && onSend) onSend(stripHtml(t) || String(t))
    },
    [
      openFormFromStrip,
      openOrderStatus,
      targetColumn,
      onSend,
      ochatbot,
      conf,
      setActiveLeadForms,
    ],
  )

  if (!allItems.length) return null

  const navBtnClass = cn(
    'shrink-0 rounded-full',
    'border border-[var(--color-border)] bg-[var(--color-surface)] shadow-md',
    compact ? 'p-1.5' : 'p-2',
    'text-[var(--color-surface-foreground)]',
    'disabled:pointer-events-none disabled:opacity-25',
  )

  const renderSlideContent = (item) => {
    switch (item.type) {
      case 'page':
      case 'product':
        return (
          <SuggestionProductOrPageCard
            item={item}
            conf={conf}
            layoutInline={layoutInline}
            layoutKey={layoutKey}
            onActivate={handleActivate}
            ochatbot={ochatbot}
            sideColumn={isSide}
            fullWidthButtons={fullWidthButtons}
            stripSize={stripSize}
          />
        )
      case 'button':
        return (
          <SuggestionMediaButtonCard
            item={item}
            conf={conf}
            layoutInline={layoutInline}
            layoutKey={layoutKey}
            onActivate={handleActivate}
            sideColumn={isSide}
            fullWidthButtons={fullWidthButtons}
            stripSize={stripSize}
          />
        )
      case 'video':
      case 'banner':
        return (
          <SuggestionCtaCard
            item={item}
            layoutInline={layoutInline}
            layoutKey={layoutKey}
            onActivate={handleActivate}
            sideColumn={isSide}
            stripSize={stripSize}
          />
        )
      case 'leadform':
        return (
          <SuggestionLeadformCard
            item={item}
            conf={conf}
            layoutInline={layoutInline}
            layoutKey={layoutKey}
            onOpen={openFormFromStrip}
            sideColumn={isSide}
            fullWidthButtons={fullWidthButtons}
            stripSize={stripSize}
          />
        )
      case 'orderstatus':
        return (
          <SuggestionOrderStatusCard
            item={item}
            conf={conf}
            layoutInline={layoutInline}
            layoutKey={layoutKey}
            onActivate={handleActivate}
            sideColumn={isSide}
            fullWidthButtons={fullWidthButtons}
            stripSize={stripSize}
          />
        )
      case 'text':
        return (
          <SuggestionTextChip
            item={item}
            onActivate={handleActivate}
            sideColumn={isSide}
          />
        )
      default:
        return (
          <SuggestionCtaCard
            item={{ ...item, title: item.title || item.text || 'Item' }}
            layoutInline={layoutInline}
            onActivate={handleActivate}
            sideColumn={isSide}
            stripSize={stripSize}
          />
        )
    }
  }

  return (
    <div
      className={cn(
        'ometrics-suggestions-strip w-full min-w-0',
        isSide &&
          'flex h-full min-h-0 flex-1 flex-col border-0 bg-transparent py-1',
        !isSide &&
          (layoutInline && isSlideOut
            ? 'mt-2 w-full min-w-0 pt-1'
            : compact
              ? 'py-1'
              : cn(
                  'border-t border-[var(--color-border)] bg-[var(--color-surface)] py-2',
                  isSlideOut && 'pt-3',
                )),
      )}
    >
      <div
        className={cn(
          'flex min-w-0 items-center',
          isSide && 'min-h-0 flex-1',
          compact ? 'gap-1' : 'gap-2',
        )}
      >
        {allItems.length > 1 ? (
          <button
            type="button"
            disabled={!canScrollPrev}
            onClick={scrollPrevCircular}
            aria-label="Previous suggestions"
            className={cn(navBtnClass, isSide && 'self-center')}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        ) : null}
        <div
          className={cn(
            'min-w-0 flex-1 overflow-hidden',
            layoutInline && 'w-full min-w-0',
            isSide && 'flex min-h-0 h-full flex-col',
          )}
          ref={emblaRef}
        >
          <div
            className={cn(
              'flex',
              layoutInline && !isSide && 'w-full min-w-0',
              isSide ? 'h-full min-h-0 items-stretch' : 'gap-2',
            )}
          >
            {allItems.map((item, index) => {
              const key = `${item.type}-${item.id ?? item.url ?? index}`
              return (
                <div
                  key={key}
                  className={cn(
                    'flex-[0_0_auto]',
                    layoutInline &&
                      !isSide &&
                      'w-[var(--suggestions-inline-slide-basis,240px)] max-w-[var(--suggestions-inline-slide-basis,240px)] shrink-0',
                    isSide &&
                      cn(
                        'box-border flex h-full min-h-0 flex-[0_0_calc(100%-0.75rem)] max-w-full items-stretch px-1.5 py-1',
                        'transition-[opacity,transform] duration-300 ease-out',
                        index !== selectedIndex && 'scale-[0.97] opacity-[0.22]',
                        index === selectedIndex && 'z-[1] opacity-100',
                      ),
                  )}
                >
                  {renderSlideContent(item)}
                </div>
              )
            })}
          </div>
        </div>
        {allItems.length > 1 ? (
          <button
            type="button"
            disabled={!canScrollNext}
            onClick={scrollNextCircular}
            aria-label="Next suggestions"
            className={cn(navBtnClass, isSide && 'self-center')}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      {isSide && allItems.length > 1 ? (
        <div
          className="flex shrink-0 justify-center gap-2 pb-1 pt-3"
          role="tablist"
          aria-label="Suggestion slides"
        >
          {allItems.map((_, i) => (
            <button
              key={String(i)}
              type="button"
              role="tab"
              aria-selected={i === selectedIndex}
              aria-label={`Suggestion ${i + 1} of ${allItems.length}`}
              className={cn(
                'h-2 w-2 rounded-full transition-opacity',
                i === selectedIndex
                  ? 'bg-[var(--color-brand)] opacity-90'
                  : 'bg-black/25 hover:opacity-70 dark:bg-white/30',
              )}
              onClick={() => emblaApi?.scrollTo(i)}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}
